import type { BoxProps } from '@chakra-ui/react';
import { Box } from '@chakra-ui/react';
import type { RefObject } from 'react';
import { useState, useCallback, useLayoutEffect } from 'react';
import type { ItemKey } from '../../types';
import { FullBox, type FullBoxAsideProps, type FullBoxRootProps } from '../FullBox';
import { SideMenuContext, useSideMenu } from './context';

export interface SideMenuRootProps extends FullBoxRootProps {
  selectedKey?: ItemKey;
  onSelectedKeyChange?: (selectedKey: ItemKey) => void;
}

export function SideMenuRoot({
  selectedKey: selectedKey_,
  onSelectedKeyChange: onSelectedKeyChange_,
  ...props
}: SideMenuRootProps) {
  const [selectedKey, setSelectedKey] = useState<ItemKey>(selectedKey_);

  const onSelectedKeyChange = useCallback(
    (selectedKey: ItemKey) => {
      setSelectedKey(selectedKey);
      onSelectedKeyChange_?.(selectedKey);
    },
    [onSelectedKeyChange_]
  );

  useLayoutEffect(() => {
    setSelectedKey(selectedKey_);
  }, [selectedKey_]);

  return (
    <SideMenuContext value={{ selectedKey, setSelectedKey, onSelectedKeyChange }}>
      <FullBox.Root column {...props}></FullBox.Root>
    </SideMenuContext>
  );
}

export interface SideMenuAsideProps extends FullBoxAsideProps {}

export function SideMenuAside(props: SideMenuAsideProps) {
  return <FullBox.Aside {...props}></FullBox.Aside>;
}

export interface SideMenuContentProps extends BoxProps {
  ref?: RefObject<HTMLElement | null>;
}

export function SideMenuContent({ className, children, ...props }: SideMenuContentProps) {
  return (
    <FullBox.Main className={className} {...props}>
      {children}
    </FullBox.Main>
  );
}

export interface SideMenuItemProps extends BoxProps {
  active?: boolean;
  itemKey: ItemKey;
}

export function SideMenuItem({
  className,
  children,
  active,
  itemKey,
  ...props
}: SideMenuItemProps) {
  const { selectedKey, onSelectedKeyChange } = useSideMenu();
  return (
    <Box
      className={className}
      aria-selected={active ?? selectedKey === itemKey}
      onClick={() => {
        onSelectedKeyChange(itemKey);
      }}
      _hover={{ bg: 'bg.subtle' }}
      _selected={{ bg: 'bg.subtle!important' }}
      transition="all 0.2s"
      cursor="pointer"
      display="block"
      {...props}
    >
      {children}
    </Box>
  );
}
